import {
  Button,
  CustomPageContainer,
  OverflowTooltip,
  TablePaginationContext,
  UnLink,
  UserAvatar,
} from '@/components';
import { PagedTable } from '@/components/PagedTable/PagedTable';
import { Spinner } from '@/components/Spinner';
import { TableRow } from '@/components/Table/types';
import { SortableColumnDef } from '@/components/TableV2/SortableTable';
import { ToggleColumn } from '@/components/ToggleColumn';
import { useToggleUserStatus } from '@/hooks/use-toggle-user-status';
import { TenantUser } from '@/modules/Users/domain';
import {
  useGetTenantUsers,
  useUpdateUserAccountAdmin,
} from '@/modules/Users/queries';
import { UserKey } from '@/modules/Users/queries/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AddAdminUserModal } from '../AddAdminUserModal';

export const ManageUsers = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const { searchTerm, page, tableSort } = useContext(TablePaginationContext);

  const { onChange: onStatusChange } = useToggleUserStatus();

  const { pageNumber, searchInput, sortBy, sortOrder } = useMemo(
    () => ({
      pageNumber: page,
      searchInput: searchTerm,
      sortBy: tableSort?.[0]?.columnId,
      sortOrder: tableSort?.[0]?.direction ?? 'ascending',
    }),
    [page, searchTerm, tableSort],
  );

  const { mutate: updateUserAccountAdmin, isLoading: isLoadingUserUpdate } =
    useUpdateUserAccountAdmin();

  const { data: users, isLoading: isLoadingUsers } = useGetTenantUsers({
    pageNumber,
    filter: searchInput,
    sortBy,
    sortOrder,
    isAccountAdmin: true,
  });

  const handleModalState = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  const handleOnDetailsClick = useCallback(
    (row: TableRow) => navigate(`/users/${row.id}`),
    [navigate],
  );

  const handleOnUnlinkClick = useCallback(
    (row: TableRow) =>
      updateUserAccountAdmin(
        {
          userIds: [Number(row.id)],
          accountAdmin: false,
        },
        {
          onError: () => {
            toast.error(
              t('error_updating_user_account_admin', {
                action: t('account_admin_remove_error_action'),
              }),
            );
          },
          onSuccess: () => {
            queryClient.invalidateQueries(UserKey.ADMIN_USERS);
            toast.success(
              t('success_updating_user_account_admin', {
                action: t('account_admin_remove_success_action'),
              }),
            );
          },
        },
      ),
    [queryClient, t, updateUserAccountAdmin],
  );

  const columns = useMemo<SortableColumnDef<TenantUser>[]>(
    () => [
      {
        id: 'name',
        name: t('name'),
        accessor: 'fullName',
        alignment: 'left',
        renderRow: (row) => (
          <div
            className="flex items-center gap-2.5 overflow-hidden"
            onClick={() => handleOnDetailsClick?.(row)}
          >
            <UserAvatar
              size="small"
              name={row.fullName}
              imageId={row.profileImageFileId}
            />
            <OverflowTooltip text={row.fullName} textClassName="font-black" />
          </div>
        ),
      },
      {
        id: 'email',
        name: t('email'),
        accessor: 'email',
      },
      {
        id: 'status',
        name: t('status'),
        accessor: 'status',
        renderRow: (row) => (
          <ToggleColumn
            onChange={(checked) => onStatusChange(checked, row.id)}
            row={row}
            value={row.status}
          />
        ),
      },
      {
        id: 'actions',
        name: t('actions'),
        sortable: false,
        renderRow: (row) =>
          isLoadingUserUpdate ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <UnLink
              className="cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOnUnlinkClick(row);
              }}
            />
          ),
      },
    ],
    [
      handleOnDetailsClick,
      handleOnUnlinkClick,
      isLoadingUserUpdate,
      onStatusChange,
      t,
    ],
  );

  return (
    <div className="flex w-full flex-col gap-5">
      <CustomPageContainer.Header title={t('manage_admin_users')} />
      <AddAdminUserModal handleClose={handleModalState} isOpen={isOpen} />
      <PagedTable
        title={t('admin_users')}
        page={users}
        isLoading={isLoadingUsers}
        columns={columns}
        emptyStateMessage={t('no_users_available')}
        actions={
          <Button onClick={handleModalState} variant="primary">
            {t('new_admin_user')}
          </Button>
        }
      />
    </div>
  );
};
